import { DEFAULT_ROLES } from '@conversed/shared/src/central/constants/permissions';
import type { CompanyMemberDTO } from '@conversed/shared/src/central/dto/company';
import type { TActivityLog } from '@conversed/shared/src/central/types/activity-log';
import type { TAssignableRole } from '@conversed/shared/src/central/types/role.types';
import { HttpStatusCode, isAxiosError } from 'axios';

export enum EMemberPendingAction {
  RoleChange = 'role-change',
  Removal = 'removal',
}

export enum EChangeMemberRoleResult {
  Changed = 'changed',
  NotMember = 'not-member',
  Outdated = 'outdated',
  Failed = 'failed',
}

export enum EUsersSort {
  Name = 'name',
  AccessGranted = 'access-granted',
}

export type TUsersFilters = {
  query: string;
  roleId: string;
  sort: EUsersSort;
};

type TRolesForChangeParams = {
  memberRoles: string[];
  selectableRoleIds: string[];
  newRoleIds: string[];
};

type TSelectableMemberRolesParams = {
  memberRoles: string[];
  selectableRoleIds: string[];
};

type TRoleUnchangedParams = TSelectableMemberRolesParams & {
  newRoleIds: string[];
};

type TMergeDefaultClientRolesParams = {
  assignableClientRoles: TAssignableRole[];
  defaultClientRoles: TAssignableRole[];
};

const MAX_INITIALS = 2;

export const getMemberFullName = (
  member: Pick<CompanyMemberDTO, 'firstName' | 'lastName' | 'email'>,
): string => {
  const fullName = [member.firstName, member.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!fullName) {
    return member.email;
  }

  return fullName;
};

export const getMemberInitials = (
  member: Pick<CompanyMemberDTO, 'firstName' | 'lastName' | 'email'>,
): string => {
  const nameParts = [member.firstName, member.lastName]
    .map((namePart) => namePart?.trim() ?? '')
    .filter(Boolean);

  if (nameParts.length === 0) {
    return member.email.slice(0, 1).toUpperCase();
  }

  return nameParts
    .map((namePart) => namePart.slice(0, 1).toUpperCase())
    .slice(0, MAX_INITIALS)
    .join('');
};

const getLabelInitials = (label: string): string =>
  label
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.slice(0, 1).toUpperCase())
    .slice(0, MAX_INITIALS)
    .join('');

type TActivityAuthorLabels = {
  staffLabel: string;
  anonymousLabel: string;
};

type TActivityAuthor = {
  name: string;
  email?: string;
  initials: string;
  isStaff?: boolean;
  profilePic?: string;
};

/**
 * A superAdmin author arrives masked from the backend, so the row shows the
 * staff label instead of a person. Every other author renders like a member
 * row on the Members tab.
 */
export const getActivityAuthor = (
  activityLog: Pick<TActivityLog, 'agent' | 'isStaffAuthor'>,
  { staffLabel, anonymousLabel }: TActivityAuthorLabels,
): TActivityAuthor => {
  if (activityLog.isStaffAuthor) {
    return {
      name: staffLabel,
      initials: getLabelInitials(staffLabel),
      isStaff: true,
    };
  }

  const author = {
    firstName: activityLog.agent.firstName ?? undefined,
    lastName: activityLog.agent.lastName ?? undefined,
    email: activityLog.agent.email ?? '',
  };

  if (!author.firstName && !author.lastName && !author.email) {
    return { name: anonymousLabel, initials: getLabelInitials(anonymousLabel) };
  }

  return {
    name: getMemberFullName(author),
    email: author.email || undefined,
    initials: getMemberInitials(author),
    profilePic: activityLog.agent.profilePic ?? undefined,
  };
};

/**
 * A superAdmin who granted access arrives masked from the backend, so the
 * table shows the staff label instead of an empty email.
 */
export const getGrantedByLabel = (
  member: Pick<CompanyMemberDTO, 'grantedBy' | 'isStaffGrantedBy'>,
  staffLabel: string,
): string => {
  if (member.isStaffGrantedBy) {
    return staffLabel;
  }

  return member.grantedBy;
};

const isSuperAdmin = (member: Pick<CompanyMemberDTO, 'roles'>): boolean =>
  member.roles.includes(DEFAULT_ROLES.SUPER_ADMIN_ROLE);

/**
 * Keeps only the members who hold at least one client-type role in the
 * selected company. Global and internal members never show on the Users page,
 * for every viewer. A superAdmin never shows either, even with a client role.
 */
export const filterClientMembers = <T extends Pick<CompanyMemberDTO, 'roles'>>(
  members: T[],
  clientRoleIds: string[],
): T[] => {
  const clientRoleIdSet = new Set(clientRoleIds);

  return members.filter(
    (member) =>
      !isSuperAdmin(member) &&
      member.roles.some((roleId) => clientRoleIdSet.has(roleId)),
  );
};

/**
 * A member who also holds an internal role is managed by Conversed, so the
 * backend refuses every change from this page. Only a member whose roles are
 * all client roles is editable here.
 */
export const isMemberEditable = (
  member: Pick<CompanyMemberDTO, 'roles'>,
  clientRoleIds: string[],
): boolean => {
  const clientRoleIdSet = new Set(clientRoleIds);

  return (
    member.roles.length > 0 &&
    member.roles.every((roleId) => clientRoleIdSet.has(roleId))
  );
};

/**
 * `/roles/assignable` returns only the roles the viewer can edit, so a viewer
 * who can only read members gets none. The default client roles fill the gap,
 * so that viewer still sees the members and their role names.
 */
export const mergeDefaultClientRoles = ({
  assignableClientRoles,
  defaultClientRoles,
}: TMergeDefaultClientRolesParams): TAssignableRole[] => {
  const assignableRoleIdSet = new Set(
    assignableClientRoles.map((role) => role.id),
  );
  const missingDefaultRoles = defaultClientRoles.filter(
    (role) => !assignableRoleIdSet.has(role.id),
  );

  return [...assignableClientRoles, ...missingDefaultRoles];
};

const matchesQuery = (
  member: Pick<CompanyMemberDTO, 'firstName' | 'lastName' | 'email'>,
  normalizedQuery: string,
): boolean => {
  if (!normalizedQuery) {
    return true;
  }

  const searchableText = [getMemberFullName(member), member.email]
    .join(' ')
    .toLowerCase();

  return searchableText.includes(normalizedQuery);
};

const getGrantedTime = (member: Pick<CompanyMemberDTO, 'grantedAt'>) => {
  if (!member.grantedAt) {
    return 0;
  }

  return new Date(member.grantedAt).getTime();
};

const compareMembers = (
  sort: EUsersSort,
): ((
  firstMember: CompanyMemberDTO,
  secondMember: CompanyMemberDTO,
) => number) => {
  if (sort === EUsersSort.AccessGranted) {
    // * Newest access first, the same order GitLab and GitHub use.
    return (firstMember, secondMember) =>
      getGrantedTime(secondMember) - getGrantedTime(firstMember);
  }

  return (firstMember, secondMember) =>
    getMemberFullName(firstMember).localeCompare(
      getMemberFullName(secondMember),
      undefined,
      { sensitivity: 'base' },
    );
};

export const filterAndSortMembers = <T extends CompanyMemberDTO>(
  members: T[],
  { query, roleId, sort }: TUsersFilters,
): T[] => {
  const normalizedQuery = query.trim().toLowerCase();

  return members
    .filter((member) => matchesQuery(member, normalizedQuery))
    .filter((member) => !roleId || member.roles.includes(roleId))
    .sort(compareMembers(sort));
};

/**
 * Replaces the member roles that the viewer can assign with the chosen roles.
 * Every other role stays, so a role change keeps the roles that the viewer
 * cannot tick. The backend refuses a save that removes such a role. The
 * request never contains `superAdmin`. The backend adds it from the global
 * role, because it is not a company role.
 */
export const buildRolesForChange = ({
  memberRoles,
  selectableRoleIds,
  newRoleIds,
}: TRolesForChangeParams): string[] => {
  const selectableRoleIdSet = new Set(selectableRoleIds);
  const keptRoles = memberRoles.filter(
    (roleId) =>
      !selectableRoleIdSet.has(roleId) &&
      roleId !== DEFAULT_ROLES.SUPER_ADMIN_ROLE,
  );

  return [...keptRoles, ...newRoleIds];
};

export const hasSameRoleIds = (
  firstRoleIds: string[],
  secondRoleIds: string[],
): boolean => {
  const firstRoleIdSet = new Set(firstRoleIds);
  const secondRoleIdSet = new Set(secondRoleIds);

  return (
    firstRoleIdSet.size === secondRoleIdSet.size &&
    [...firstRoleIdSet].every((roleId) => secondRoleIdSet.has(roleId))
  );
};

/**
 * The member roles the viewer can tick. A role the viewer cannot assign is
 * left out here, and `buildRolesForChange` keeps it on save.
 */
export const getSelectableMemberRoles = ({
  memberRoles,
  selectableRoleIds,
}: TSelectableMemberRolesParams): string[] => {
  const selectableRoleIdSet = new Set(selectableRoleIds);

  return memberRoles.filter((roleId) => selectableRoleIdSet.has(roleId));
};

export const isRoleUnchanged = ({
  memberRoles,
  selectableRoleIds,
  newRoleIds,
}: TRoleUnchangedParams): boolean =>
  hasSameRoleIds(
    getSelectableMemberRoles({ memberRoles, selectableRoleIds }),
    newRoleIds,
  );

// * The invite endpoint answers 409 when the email already belongs to a member
// * of the selected company.
export const isAlreadyMemberError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict;

// * A role change answers 404 when the member left the selected company after
// * this page loaded the list.
export const isNotMemberError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound;

// * A role change answers 409 when the member roles changed after this page
// * loaded the list.
export const isOutdatedRolesError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict;
